/**
 * Static plan display catalog for the Plans / Pricing screen.
 *
 * Runtime config (`getRuntimeConfig().stripePlans`) carries only non-secret
 * price identifiers (Stripe price IDs / lookup keys) — never the dollar amounts
 * or the marketing copy. This module holds the human-facing display metadata
 * (prices, taglines, feature lists, featured/coming-soon flags) as a static
 * constant keyed by {@link Tier}, so the app's Plans screen can match the
 * marketing site without shipping prices through config.
 *
 * Prices are quoted in AUD. A tier is only purchasable when it is BOTH present
 * here (with `comingSoon: false`) AND configured in `stripePlans`; the catalog
 * decides what is shown, the config decides what can actually check out.
 */

import type { Tier, PlanId } from '@covered-calls/shared';

/** Display metadata for a single plan tier. */
export interface PlanCatalogEntry {
  /** Internal tier key. */
  tier: Tier;
  /** Human-readable tier name, e.g. 'Trade Basic'. */
  displayName: string;
  /** One-line positioning statement shown under the name. */
  tagline: string;
  /** Monthly price, formatted for display, e.g. '$9.99'. */
  monthlyPrice: string;
  /** Annual price, formatted for display, e.g. '$99.90'. */
  annualPrice: string;
  /** Feature bullet points shown with tick marks. */
  features: string[];
  /** When true, the tier is not yet purchasable and shows a "Coming soon" CTA. */
  comingSoon: boolean;
  /** When true, the tier is visually highlighted as the recommended pick. */
  featured: boolean;
}

/**
 * The plan tiers in canonical display order. Prices are AUD; annual prices give
 * two months free relative to the monthly rate (see {@link ANNUAL_DISCOUNT_NOTE}).
 */
export const PLAN_CATALOG: PlanCatalogEntry[] = [
  {
    tier: 'trade_basic',
    displayName: 'Trade Basic',
    tagline: 'Everything, unlimited trades.',
    monthlyPrice: '$9.99',
    annualPrice: '$99.90',
    features: [
      'Unlimited trades & positions',
      'Live IBKR pricing & auto-refresh',
      'Rolling guidance & IV insight',
      'Commission schedules & GST',
      'CGT & portfolio reporting',
      'Trade report uploads',
    ],
    comingSoon: false,
    featured: true,
  },
  {
    tier: 'trade_execution',
    displayName: 'Trade Execution',
    tagline: 'Place broker orders from your plan.',
    monthlyPrice: '$15.99',
    annualPrice: '$159.90',
    features: [
      'Everything in Trade Basic',
      'Broker order placement — details coming soon',
      'More features to be announced',
    ],
    comingSoon: true,
    featured: false,
  },
  {
    tier: 'trade_discovery',
    displayName: 'Trade Discovery',
    tagline: 'AI-assisted trade ideas.',
    monthlyPrice: '$19.99',
    annualPrice: '$199.90',
    features: [
      'Everything in Trade Execution',
      'AI trade discovery — details coming soon',
      'More features to be announced',
    ],
    comingSoon: true,
    featured: false,
  },
];

/** Prominent note shown alongside the annual price to highlight the discount. */
export const ANNUAL_DISCOUNT_NOTE = 'Billed annually — 2 months free';

/**
 * Human labels for every {@link PlanId}, including `trial` (which is not part of
 * {@link PLAN_CATALOG} because it is not purchasable). Used wherever a plan id
 * needs to be rendered as a name outside the pricing cards.
 */
const PLAN_DISPLAY_NAMES: Record<PlanId, string> = {
  trial: 'Free Trial',
  trade_basic: 'Trade Basic',
  trade_execution: 'Trade Execution',
  trade_discovery: 'Trade Discovery',
};

/**
 * Maps any {@link PlanId} to its human-readable display name. Falls back to
 * 'Free Trial' for an unrecognised id so callers never render a raw key.
 */
export function planDisplayName(planId: PlanId): string {
  return PLAN_DISPLAY_NAMES[planId] ?? 'Free Trial';
}

/**
 * Per-plan accent design token (a `var(--color-*)` string) used to tint each
 * tier's shield icon so tiers are visually distinguishable. Token-only by
 * design — the values reference existing tokens in `styles/global.css :root`.
 */
export const PLAN_ACCENT_TOKEN: Record<PlanId, string> = {
  trial: 'var(--color-muted)',
  trade_basic: 'var(--color-brand)',
  trade_execution: 'var(--color-accent)',
  trade_discovery: 'var(--color-success)',
};
